/**
 * useSyncPatientToInsurance Hook
 *
 * Enhanced hook to sync patient information to insurance card
 * Based on relationship type with special handling for family names
 *
 * Matches RA2's target_culumn_value_ctl() and related functions
 */

import { useEffect, useCallback, useRef } from "react";
import { UseFormWatch, UseFormSetValue } from "react-hook-form";
import { PatientFormSchemaType } from "@/pages/main/patient/types";
import { extractFamilyName } from "@/utils/japaneseText";

export interface UseSyncPatientToInsuranceOptions {
  /** Whether to enable syncing (default: true) */
  enabled?: boolean;
}

/**
 * Hook to sync patient info to insurance card based on relationship type
 *
 * When relationship is "本人" (1) or "67（本人）" (3):
 * - All patient info is synced to insurance card
 * - Insurance fields become readonly
 *
 * When relationship is "家族" (2):
 * - Only family name (first part of name) is synced
 * - Insurance fields are editable
 */
export function useSyncPatientToInsurance(
  watch: UseFormWatch<PatientFormSchemaType>,
  setValue: UseFormSetValue<PatientFormSchemaType>,
  options: UseSyncPatientToInsuranceOptions = {},
): {
  isInsuredPerson: boolean;
  syncPatientToInsurance: () => void;
  clearInsuranceFields: () => void;
} {
  const { enabled = true } = options;
  const previousRelationshipRef = useRef<number | null>(null);
  const isInitialSyncDone = useRef(false);

  const relationshipId = watch("health_insurance_card.relationship_id");

  // Patient information fields
  const patientName = watch("patient_information.name");
  const patientNameKana = watch("patient_information.name_kana");
  const patientSexId = watch("patient_information.sex_id");
  const patientDateOfBirth = watch("patient_information.date_of_birth");
  const patientPostalCode1 = watch("patient_information.postal_code1");
  const patientPostalCode2 = watch("patient_information.postal_code2");
  const patientAddress1 = watch("patient_information.address1");
  const patientAddress2 = watch("patient_information.address2");
  const patientAddress3 = watch("patient_information.address3");
  const patientAddressName = watch("patient_information.address_name");
  const patientAddressKana = watch("patient_information.address_kana");
  const patientTelephone = watch("patient_information.telephone");

  // Whether the insured person is the patient themselves
  const isInsuredPerson = relationshipId === 1 || relationshipId === 3;

  /**
   * Clear insurance card fields when switching from 本人 to 家族
   */
  const clearInsuranceFields = useCallback(() => {
    setValue("health_insurance_card.name", "");
    setValue("health_insurance_card.name_kana", "");
    setValue("health_insurance_card.sex_id", undefined);
    setValue("health_insurance_card.date_of_birth", "");
    setValue("health_insurance_card.postal_code1", "");
    setValue("health_insurance_card.postal_code2", "");
    setValue("health_insurance_card.address1", "");
    setValue("health_insurance_card.address2", "");
    setValue("health_insurance_card.address_kana", "");
    setValue("health_insurance_card.telephone", "");
  }, [setValue]);

  /**
   * Sync patient information to insurance card
   */
  const syncPatientToInsurance = useCallback(() => {
    if (!enabled) return;

    if (isInsuredPerson) {
      // 本人 or 67（本人）: Sync all fields (names only if 受診者氏名・カナ have content — 複写新規など)
      const pn = (patientName ?? "").trim();
      const pk = (patientNameKana ?? "").trim();
      if (pn || pk) {
        setValue("health_insurance_card.name", patientName);
        setValue("health_insurance_card.name_kana", patientNameKana);
      }
      setValue("health_insurance_card.sex_id", patientSexId);
      setValue("health_insurance_card.date_of_birth", patientDateOfBirth);
      setValue("health_insurance_card.postal_code1", patientPostalCode1);
      setValue("health_insurance_card.postal_code2", patientPostalCode2);
      setValue("health_insurance_card.address1", patientAddress1);

      // Combine address2, address3, and address_name for insurance card
      const combinedAddress = [
        patientAddress2,
        patientAddress3,
        patientAddressName,
      ]
        .filter(Boolean)
        .join(" ");
      setValue("health_insurance_card.address2", combinedAddress);
      setValue("health_insurance_card.address_kana", patientAddressKana);
      setValue("health_insurance_card.telephone", patientTelephone);
    } else if (relationshipId === 2) {
      // 家族: Only sync family name (first part before space)
      // RA2: No269 続柄家族の場合被保険者名とカナは苗字のみでよい
      const pn = (patientName ?? "").trim();
      const pk = (patientNameKana ?? "").trim();
      if (!pn && !pk) {
        return;
      }
      const familyName = extractFamilyName(patientName || "");
      const familyNameKana = extractFamilyName(patientNameKana || "");

      setValue("health_insurance_card.name", familyName);
      setValue("health_insurance_card.name_kana", familyNameKana);
      // Don't sync other fields for family members
    } else {
      // 未選択 (no relationship selected): copy full name + kana (A2 parity)
      const pn = (patientName ?? "").trim();
      const pk = (patientNameKana ?? "").trim();
      if (pn || pk) {
        setValue("health_insurance_card.name", patientName);
        setValue("health_insurance_card.name_kana", patientNameKana);
      }
    }
  }, [
    enabled,
    isInsuredPerson,
    relationshipId,
    patientName,
    patientNameKana,
    patientSexId,
    patientDateOfBirth,
    patientPostalCode1,
    patientPostalCode2,
    patientAddress1,
    patientAddress2,
    patientAddress3,
    patientAddressName,
    patientAddressKana,
    patientTelephone,
    setValue,
  ]);

  // Effect: Sync when patient info changes and relationship is 本人
  useEffect(() => {
    if (!enabled) return;

    if (isInsuredPerson) {
      syncPatientToInsurance();
    }
  }, [
    enabled,
    isInsuredPerson,
    patientName,
    patientNameKana,
    patientSexId,
    patientDateOfBirth,
    patientPostalCode1,
    patientPostalCode2,
    patientAddress1,
    patientAddress2,
    patientAddress3,
    patientAddressName,
    patientAddressKana,
    patientTelephone,
    syncPatientToInsurance,
  ]);

  // Effect: Handle relationship change
  useEffect(() => {
    if (!enabled) return;

    const previousRelationship = previousRelationshipRef.current;
    previousRelationshipRef.current = relationshipId;

    // Skip initial sync detection
    if (!isInitialSyncDone.current) {
      isInitialSyncDone.current = true;
      if (isInsuredPerson) {
        syncPatientToInsurance();
      }
      return;
    }

    // If switching from 家族 to 本人, sync all fields
    if (previousRelationship === 2 && isInsuredPerson) {
      syncPatientToInsurance();
    }

    // If switching from 本人 to 家族, clear fields first then set family name
    if (
      (previousRelationship === 1 || previousRelationship === 3) &&
      relationshipId === 2
    ) {
      clearInsuranceFields();
      // Then set just the family name
      const familyName = extractFamilyName(patientName || "");
      const familyNameKana = extractFamilyName(patientNameKana || "");
      setValue("health_insurance_card.name", familyName);
      setValue("health_insurance_card.name_kana", familyNameKana);
    }

    // If switching to 本人 from any state
    if (isInsuredPerson && previousRelationship !== relationshipId) {
      syncPatientToInsurance();
    }
  }, [
    enabled,
    relationshipId,
    isInsuredPerson,
    syncPatientToInsurance,
    clearInsuranceFields,
    patientName,
    patientNameKana,
    setValue,
  ]);

  return {
    isInsuredPerson,
    syncPatientToInsurance,
    clearInsuranceFields,
  };
}

/**
 * Hook to handle name field changes with family name extraction
 */
export function useNameFieldSync(
  watch: UseFormWatch<PatientFormSchemaType>,
  setValue: UseFormSetValue<PatientFormSchemaType>,
): {
  handleNameChange: () => void;
  handleNameKanaChange: () => void;
} {
  const relationshipId = watch("health_insurance_card.relationship_id");

  const handleNameChange = useCallback(() => {
    const patientName = watch("patient_information.name");
    const patientNameKana = watch("patient_information.name_kana");
    if (!(patientName ?? "").trim() && !(patientNameKana ?? "").trim()) {
      return;
    }

    if (relationshipId === 2) {
      // 家族: Copy only family name
      const familyName = extractFamilyName(patientName || "");
      const familyNameKana = extractFamilyName(patientNameKana || "");
      setValue("health_insurance_card.name", familyName);
      setValue("health_insurance_card.name_kana", familyNameKana);
    } else {
      // 本人 (1) / 67（本人）(3) / 未選択: Copy full name
      setValue("health_insurance_card.name", patientName);
    }
  }, [watch, setValue, relationshipId]);

  const handleNameKanaChange = useCallback(() => {
    const patientName = watch("patient_information.name");
    const patientNameKana = watch("patient_information.name_kana");
    if (!(patientName ?? "").trim() && !(patientNameKana ?? "").trim()) {
      return;
    }

    if (relationshipId === 2) {
      // 家族: Copy only family name kana
      const familyNameKana = extractFamilyName(patientNameKana || "");
      setValue("health_insurance_card.name_kana", familyNameKana);
    } else {
      // 本人 (1) / 67（本人）(3) / 未選択: Copy full name kana
      setValue("health_insurance_card.name_kana", patientNameKana);
    }
  }, [watch, setValue, relationshipId]);

  return {
    handleNameChange,
    handleNameKanaChange,
  };
}

export default useSyncPatientToInsurance;
